import { MultiSelect } from '@components/ui/multi-select';
import { useStore } from '@tanstack/react-form';
import { useFieldContext, useFormContext } from './form';
import { FormErrorMessage, FormField, FormLabel } from './shared.component';

type SelectMultipleFieldProps = Omit<React.ComponentProps<typeof MultiSelect>, 'onValueChange' | 'value'> & {
  className?: string;
  label?: string;
  labelSuffix?: React.ReactNode;
  placeholder?: string;
  required?: boolean;
  tooltip?: React.ReactNode;
};

/**
 * A wrapper component over the basic SelectMultiple that adds TanStack Form logic and validation.
 * Provides form field context, error handling, accessibility features, and multi-select functionality.
 * @param props - Standard select multiple props plus optional styling and configuration
 * @returns A form-integrated multi-select field component with dropdown options
 */
export function SelectMultipleField(props: SelectMultipleFieldProps) {
  const {
    className,
    label,
    labelSuffix,
    options,
    placeholder = 'Select options',
    required,
    tooltip,
    ...otherProps
  } = props;
  const field = useFieldContext<string[]>();
  const form = useFormContext();

  const isSubmitting = useStore(form.store, (state) => state.isSubmitting);
  const isDisabled = isSubmitting || Boolean(otherProps.disabled);

  return (
    <FormField className={className}>
      {label && (
        <FormLabel
          htmlFor={field.name}
          labelSuffix={labelSuffix}
          required={required}
          tooltip={tooltip}
        >
          {label}
        </FormLabel>
      )}
      <MultiSelect
        {...otherProps}
        id={field.name}
        value={field.state.value ?? []}
        disabled={isDisabled}
        hasError={!field.state.meta.isValid}
        onValueChange={(values) => field.handleChange(values)}
        placeholder={placeholder}
        options={options}
        trailingAction={isDisabled ? undefined : otherProps.trailingAction}
      />
      <FormErrorMessage />
    </FormField>
  );
}
